import { createFileRoute, notFound } from "@tanstack/react-router";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPostsQueryOptions,
  blogToolQueryOptions,
} from "@/tools/blog/hooks/use-blog-data";
import { pageDeps, pageSearch } from "@/tools/blog/lib/page-search";
import { BlogTool } from "@/tools/blog/pages/BlogTool";

export const Route = createFileRoute("/blog/$tool/")({
  component: BlogToolRoute,
  ssr: true,
  validateSearch: pageSearch,
  loaderDeps: pageDeps,
  loader: async ({ context, params, deps }) => {
    const locale = getLocale();
    const tool = await context.queryClient.ensureQueryData(
      blogToolQueryOptions(params.tool, locale),
    );
    // Unknown tool slug → real 404 (was a 200 shell = soft 404).
    if (!tool) throw notFound();
    const postsPage = await context.queryClient.ensureQueryData(
      blogPostsQueryOptions(locale, params.tool, undefined, deps.page),
    );
    // Past the last page → 404 rather than an empty listing.
    if (deps.page > 1 && postsPage.items.length === 0) throw notFound();
    return { tool, total: postsPage?.total ?? 0 };
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.tool?.name ?? params.tool;
    const basePath = `/blog/${params.tool}`;
    return {
      meta: [
        // A category hub with no posts yet is a thin page — keep it out of
        // the index until the first article lands.
        ...(loaderData && loaderData.total === 0
          ? [{ name: "robots", content: "noindex, follow" }]
          : []),
        { title: `${name} | Toolverse Blog` },
        ...(loaderData?.tool?.description
          ? [{ name: "description", content: loaderData.tool.description }]
          : []),
        ...i18nOgMeta(basePath),
      ],
      links: [...i18nLinks(basePath)],
    };
  },
});

function BlogToolRoute() {
  const { tool } = Route.useParams();
  const { page } = Route.useSearch();
  return <BlogTool tool={tool} page={page ?? 1} />;
}
